const post = {
  "slug": "add-team-members-to-checkout-dm",
  "title": "How to Add Team Members to Checkout DM (Pro)",
  "seoTitle": "Add Team Members to Checkout DM (Pro)",
  "description": "How inviting teammates works on the Pro plan: the invite, how members log in, what members can and cannot do and how extra seats are billed and expire.",
  "excerpt": "Pro lets you share the dashboard with two teammates. Here is how the invite works, exactly what a member can and cannot do and how extra seats are handled.",
  "category": "Product",
  "tags": [
    "Team access",
    "Checkout DM Pro",
    "Instagram shop team",
    "Member permissions"
  ],
  "date": "2026-11-16",
  "cover": {
    "src": "/blog-images/add-team-members-to-checkout-dm-cover.png",
    "alt": "A team invite email and a member logging in with a one-time link"
  },
  "blocks": [
    {
      "type": "p",
      "text": "When an Instagram shop grows, one person can no longer answer every chat and confirm every payment. Team access on the Pro plan lets you share the dashboard with teammates while keeping money and settings in your own hands. This guide explains how it works."
    },
    {
      "type": "h2",
      "text": "Who is included"
    },
    {
      "type": "p",
      "text": "Team is a Pro-plan feature. Pro includes 3 team members: you as the owner plus 2 members. The owner is whoever connected the Instagram account, and everyone else is a Member. There is one role today, and every invite joins with the same permissions. If you need a third pair of hands, the owner can add an extra seat from the Team page as a small monthly add-on, paid through Razorpay."
    },
    {
      "type": "h2",
      "text": "From invite to logged in"
    },
    {
      "type": "ol",
      "items": [
        "**The owner sends the invite.** From the Team page, enter a name and an email and send it. The person gets an email with an Accept link. The link is valid for 48 hours. If it expires, remove the pending invite and send a fresh one. The same email can be invited again right away.",
        "**The member accepts.** Clicking Accept logs them straight into the dashboard with no separate login step. That first session lasts 24 hours.",
        "**Logging in after that.** Members log back in from the team login page: they enter the email the invite was sent to and a one-time login link is emailed to them. The link works once and expires after 1 hour, like a password-reset link. There is no password to remember."
      ]
    },
    {
      "type": "h2",
      "text": "What members can do"
    },
    {
      "type": "p",
      "text": "Members handle the day-to-day work: replying to customers, confirming payments, logging returns and keeping the catalog current."
    },
    {
      "type": "ul",
      "items": [
        "View and reply in Chats",
        "View and edit Customers",
        "Confirm or reject payments and log returns in Orders and Returns",
        "View and manage the Catalog",
        "View Analytics"
      ]
    },
    {
      "type": "h2",
      "text": "What stays with the owner"
    },
    {
      "type": "p",
      "text": "Anything that touches money, settings or who else has access is owner only:"
    },
    {
      "type": "ul",
      "items": [
        "Inviting, removing or viewing the Team page",
        "Creating or editing Automations and Comment-to-DM rules",
        "Changing discount codes or the shipping fee",
        "Business Settings: bank details, UPI, Razorpay keys and the Instagram connection",
        "Billing, plan changes and payment history"
      ]
    },
    {
      "type": "p",
      "text": "These limits are not just hidden in the menu. The server checks and blocks owner-only actions directly, so even a direct link to one of those pages sends a member back to the dashboard. That makes it safe to give someone access to customer chats without handing over your payment details."
    },
    {
      "type": "h2",
      "text": "Which jobs to hand to a teammate"
    },
    {
      "type": "p",
      "text": "The permission list maps neatly onto the daily work of a busy shop. A member can answer customers in Chats, correct a customer\u2019s saved details in Customers, confirm or reject manual payments and log returns in Orders and Returns, keep products up to date in the Catalog and read Analytics. You keep everything that moves money or changes how the shop runs. In other words, a teammate can run the day-to-day while you decide pricing, discounts, payment details and who has access."
    },
    {
      "type": "h2",
      "text": "What a member sees"
    },
    {
      "type": "p",
      "text": "Members open the same dashboard, limited to the tabs and actions above. The Team Access Guide is open to both owners and members, so a member who wonders what they are allowed to do can read exactly that without asking you. If all your seats are taken, the Team page tells you to add another seat before you can invite someone else."
    },
    {
      "type": "h2",
      "text": "Extra seats"
    },
    {
      "type": "p",
      "text": "Extra seats work on a shared clock. Every extra seat you buy expires on the same date, so adding a second or third extra seat does not start a separate 30-day period of its own. If you buy one while another is still active, you are charged only for the days left until that shared expiry date, never a full month again for the same seat. Before any payment goes through, you see the exact price and the exact date your seats will expire."
    },
    {
      "type": "p",
      "text": "The owner receives a reminder email 1 to 3 days before extra seats expire. If they are not renewed by then, the most recently added members are automatically signed out and the extra seats removed. Everyone else on the team, including you, is unaffected. To keep seats going, buy again from the Team page, before or after they lapse."
    },
    {
      "type": "callout",
      "tone": "lilac",
      "title": "Why this helps with payments",
      "text": "Confirming manually reported payments is one of the jobs members can do. If you accept UPI or bank transfers, a teammate can check screenshots and confirm orders while you stay in control of bank details and settings. Our guide to [avoiding fake payment screenshots](/blog/avoid-fake-payment-screenshots-instagram-sellers) explains what to look for."
    },
    {
      "type": "p",
      "text": "If you are comparing plans before deciding, our overview of [Checkout DM pricing and plans](/blog/checkout-dm-pricing-and-plans-explained) lists everything Pro includes."
    }
  ],
  "related": [
    {
      "to": "/team-guide",
      "title": "Team Access Guide",
      "description": "How invites, access and seats work in full."
    },
    {
      "to": "/tabs-guide",
      "title": "Dashboard Tabs Guide",
      "description": "What each tab means for you and your team."
    },
    {
      "to": "/pricing",
      "title": "Pricing",
      "description": "See what Pro includes."
    }
  ],
  "faqs": [
    {
      "q": "How many team members does Pro include?",
      "a": "Pro includes 3 team members: the owner plus 2 members. More seats are available as a monthly add-on."
    },
    {
      "q": "Can a member change my bank details or billing?",
      "a": "No. Business Settings, billing and plan changes, discount codes, shipping fee and Automations are owner only."
    },
    {
      "q": "How long is the invite link valid?",
      "a": "48 hours. If it expires, remove the pending invite and send a new one. The same email can be invited again right away."
    },
    {
      "q": "How do members log in after the first session?",
      "a": "They enter their invited email on the team login page and receive a one-time login link that expires after 1 hour. There is no password."
    },
    {
      "q": "What happens when extra seats expire?",
      "a": "The most recently added members are signed out and the extra seats removed. The owner and everyone else are unaffected. You get a reminder email 1 to 3 days before."
    }
  ]
};

export default post;
